/**
 * invioConTentativi.js — un invio che la rete fa cadere riparte da solo,
 * fino a tre tentativi; poi lo dice (proposta telefono-stile-nuovo, punto 1:
 * «mai un file perso in silenzio»).
 *
 * Si riprova SOLO quando la risposta non e' arrivata (la rete, un proxy che
 * non risponde: 502, 503, 504). Un rifiuto del server - un tipo che i byte
 * non confermano, un file troppo grande - non cambia riprovando: si dice
 * subito, col suo motivo.
 *
 * Se la risposta si perde DOPO che il server ha archiviato, il tentativo
 * dopo NON archivia una seconda volta (migration 347, 29/10/2026): ogni
 * invio ha un identificativo suo (`nuovoInvioId`), uguale a ogni tentativo
 * e a «Riprova», che parte nell'intestazione X-Invio-Id; il server lo
 * riconosce e risponde con la riga della prima volta. Un invio nuovo ne ha
 * uno nuovo. Fino a quel giorno si era scelto il doppione al file perso.
 */
export const INTESTAZIONE_INVIO = 'X-Invio-Id';

/* 128 bit dal generatore del browser, nella forma di un UUID (versione 4).
   Senza un generatore sicuro niente identificativo: l'invio parte senza
   intestazione, come prima, e un doppione resta possibile. */
export function nuovoInvioId() {
    const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    if (!c || typeof c.getRandomValues !== 'function') return null;
    const b = c.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
export const TENTATIVI = 3;
/* Un oggetto e non costanti: la prova accorcia le attese. */
export const ATTESE = { ms: [2000, 5000] };

/* La risposta non e' arrivata. Anche quando l'intercettore di services/api.js
   ha gia' vestito l'errore di rete con un `response` (per dare
   `response.data.message` a chi lo legge): li' lo `status` e' vuoto. Fino al
   3/11/2026 quel caso - la rete caduta davvero - non contava come «non
   arrivata»: niente ripresa, e il messaggio diceva «Errore di connessione»
   (trovato lavorando sulla prima lettura delle pagine). */
export const eDiRete = (e) => !e?.response || !e.response.status
    || [502, 503, 504].includes(e.response.status);

const pausa = (ms) => new Promise((r) => setTimeout(r, ms));

/* Senza rete non si brucia un tentativo: si aspetta che torni. */
function reteTornata() {
    if (typeof navigator === 'undefined' || navigator.onLine !== false) return Promise.resolve();
    return new Promise((risolvi) => {
        const torna = () => { window.removeEventListener('online', torna); risolvi(); };
        window.addEventListener('online', torna);
    });
}

/**
 * Esegue `invio(tentativo)` fino a TENTATIVI volte. `sulTentativo(n)` dice
 * a chi disegna quale tentativo parte. Solleva l'ultimo errore.
 */
export async function conTentativi(invio, { sulTentativo, tentativi = TENTATIVI } = {}) {
    let ultimo = null;
    for (let n = 1; n <= tentativi; n += 1) {
        await reteTornata();
        if (sulTentativo) sulTentativo(n);
        try {
            return await invio(n);
        } catch (e) {
            ultimo = e;
            if (!eDiRete(e) || n === tentativi) break;
            await pausa(ATTESE.ms[Math.min(n - 1, ATTESE.ms.length - 1)]);
        }
    }
    throw ultimo;
}
